///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { findIndex, isEqual, cloneDeep, omit, map } from "lodash";

import { fetchBidSummary } from "../../slices/bidSummarySlice";
import {
  fetchBidDetails,
  updateBidDetailsValidation,
  updateUnsavedBidDetailsChanges,
  resetState,
  saveBidDetails,
  resetUpdateBidDetailsState,
  updateBid
} from "../../slices/bidDetailsSlice";
import Accordion from "../form-controls/Accordion";
import LoadingErrorState from "../Bid/details/LoadingErrorState";
import ReviewBid from "../new-bid/ReviewBid";
import NavigationBlocker from "../NavigationBlocker";
import ConfirmCancellationModal from "./details/ConfirmCancellationModal";
import SystemErrorModal from "../SystemErrorModal";
import reverseMapBidDetails from "../../helpers/reverseMapBidDetails";
import mapBidDetails from "../../helpers/mapBidDetails";
import {
  handleNewBidValidation,
  handleStrategicQuantileValidation,
  removeNewBidValidation
} from "../../helpers/handleNewBidValidation";
import Spinner from "../icons/Spinner";
import getModeForUserRole from "../../helpers/getModeForUserRole";
import { isClosedReopenBlockedByExceedingTimeLimit } from "../../helpers/disableRFQStatus";
import messages from "../../helpers/messages";
import DeleteBidModal from "./details/DeleteBidModal";

class Details extends Component {
  state = {
    initialState: null,
    newBidState: null,
    modalOpen: false,
    deleteBidModal: false
  };

  componentDidMount() {
    const {
      dispatch,
      match: {
        params: { bidId }
      }
    } = this.props;
    dispatch(fetchBidSummary({ bidId }));
    dispatch(fetchBidDetails(bidId));
    window.addEventListener("beforeunload", this.beforeunload);
  }

  componentDidUpdate(prevProps) {
    const { details, dispatch, summary } = this.props;
    if (details !== prevProps.details || summary !== prevProps.summary) {
      const bidDetails =
        details && summary && reverseMapBidDetails(details, summary.division);
      this.setState({
        initialState: cloneDeep(bidDetails),
        newBidState: cloneDeep(bidDetails)
      });
      dispatch(updateBid(cloneDeep(bidDetails)));
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    window.removeEventListener("beforeunload", this.beforeunload);
    dispatch(resetState());
  }

  beforeunload = event => {
    if (this.props.unsavedChanges) {
      // Cancel the event as stated by the standard.
      event.preventDefault();
      // Chrome requires returnValue to be set.
      event.returnValue = "";
    }
  };

  handleUpdateBidDetails = () => {
    const {
      dispatch,
      match: {
        params: { bidId }
      },
      role,
      bid,
      summary
    } = this.props;

    //find out bidModeId if RFQ Status was edited
    const modeType = getModeForUserRole(role);
    const initialBidMode = this.state.initialState.modes.find(
      ({ mode }) => mode === modeType
    );
    const updatedBidMode = bid.modes.find(({ mode }) => mode === modeType);
    let bidModeId = null;
    if (
      !isEqual(initialBidMode?.currentStatus, updatedBidMode?.currentStatus)
    ) {
      if (!isClosedReopenBlockedByExceedingTimeLimit(updatedBidMode, summary)) {
        bidModeId = updatedBidMode.bidModeId;
      }
    }

    //find out whether bid was edited other than RFQ Status
    const oldDetails = {
      ...this.state.initialState,
      modes: map(this.state.initialState.modes, o => omit(o, ["currentStatus"]))
    };
    const newDetails = {
      ...bid,
      modes: map(bid.modes, o => omit(o, ["currentStatus"]))
    };
    const bidWasEdited = !isEqual(oldDetails, newDetails);
      const { crmEnableImdlUi } = this.props.featureFlags;
    dispatch(
        saveBidDetails(mapBidDetails(bid, false, crmEnableImdlUi), bidId, role, bidModeId, bidWasEdited)
    );
  };

  handleSystemErrorExit = () => {
    const {
      dispatch,
      match: {
        params: { bidId }
      }
    } = this.props;
    dispatch(resetUpdateBidDetailsState());
    dispatch(fetchBidDetails(bidId));
  };

  handleConfirmCancellationExit = () => {
    const { dispatch } = this.props;
    this.setState({
      modalOpen: false,
      newBidState: cloneDeep(this.state.initialState)
    });
    dispatch(updateBid(cloneDeep(this.state.initialState)));
  };

  handleOnSave = () => {
    const { validationData, dispatch, bid, featureFlags } = this.props;
    const validation = handleNewBidValidation(
      4,
      bid,
      validationData,
      this.props.summary.division,
      featureFlags.crmEnableImdlUi
    );

    dispatch(updateBidDetailsValidation(validation.validationData));
    if (validation.allValid) {
      this.handleUpdateBidDetails();
    }
  };

  setBidState = (property, value) => {
    const { dispatch } = this.props;
    const { initialState, newBidState } = this.state;
    let stateObj = newBidState;
    stateObj[property] = value;
    this.setState({ newBidState: stateObj });
    dispatch(updateBid({ ...stateObj }));
    dispatch(
      updateUnsavedBidDetailsChanges(!isEqual(newBidState, initialState))
    );
  };

  setModeDetails = (mode, field, value) => {
    const updatedModes = [...this.state.newBidState.modes];
    const modeIndex = findIndex(updatedModes, { mode });
    updatedModes[modeIndex] = {
      ...updatedModes[modeIndex],
      [field]: value
    };
    this.setBidState("modes", updatedModes);
  };

  removeError = (field, childField) => {
    const { validationData, dispatch } = this.props;
    const validation = removeNewBidValidation(
      4,
      validationData,
      field,
      childField
    );
    dispatch(updateBidDetailsValidation(validation.validationData));
  };

  validateStrategicQuantile = (minValue, maxValue) => {
    const { validationData, dispatch } = this.props;
    const validation = handleStrategicQuantileValidation(
      this.state.newBidState,
      cloneDeep(validationData),
      minValue,
      maxValue
    );

    dispatch(updateBidDetailsValidation(validation));
  };

  render() {
    const {
      detailsLoading,
      detailsError,
      validationData,
      updateBidDetailsLoading,
      updateBidDetailsFailure,
      role,
      summary,
      user
    } = this.props;
    const { initialState, modalOpen, newBidState, deleteBidModal } = this.state;
    const areEqual = isEqual(newBidState, initialState) && !modalOpen;

    return (
      <Fragment>
        <NavigationBlocker when={!areEqual} />
        <ConfirmCancellationModal
          open={modalOpen}
          closeModal={() => {
            this.setState({ modalOpen: false });
          }}
          handleExit={this.handleConfirmCancellationExit}
        />
        <SystemErrorModal
          open={updateBidDetailsFailure}
          closeModal={this.handleSystemErrorExit}
          handleExit={this.handleSystemErrorExit}
          handleTryAgain={this.handleUpdateBidDetails}
        />
        {initialState?.rfqName && deleteBidModal && (
          <DeleteBidModal
            open={true}
            handleClose={() => {
              this.setState({ deleteBidModal: false });
            }}
            bidId={initialState.bidId}
            bidName={initialState.rfqName}
          />
        )}
        <div className="bid page-container mb-24 mt-28">
          <div className="details new-bid-sections">
            {(detailsLoading || detailsError) && (
              <Fragment>
                <Accordion title={messages.bidInformation.defaultMessage}>
                  <LoadingErrorState
                    error={detailsError}
                    loading={detailsLoading}
                  />
                </Accordion>
                <Accordion title={messages.modeInformation.defaultMessage}>
                  <LoadingErrorState
                    error={detailsError}
                    loading={detailsLoading}
                  />
                </Accordion>
                <Accordion title={messages.bidStrategy.defaultMessage}>
                  <LoadingErrorState
                    error={detailsError}
                    loading={detailsLoading}
                  />
                </Accordion>
              </Fragment>
            )}
            {newBidState && (
              <ReviewBid
                bid={newBidState}
                setBidState={this.setBidState}
                setModeDetails={this.setModeDetails}
                validationData={validationData}
                removeError={this.removeError}
                isBidView={true}
                initialRfqName={initialState.rfqName}
                role={role}
                summary={summary}
                validateStrategicQuantile={this.validateStrategicQuantile}
              />
            )}
            <div className="one-whole mb-12">
              {user.isAdmin && (
                <button
                  className="text-button delete-button univers-67-bold-cond"
                  onClick={() => {
                    this.setState({ deleteBidModal: true });
                  }}
                >
                  {messages.delete.defaultMessage}
                </button>
              )}

              {!areEqual && (
                <div className="fr">
                  <button
                    className="secondary-button univers-67-bold-cond bg-white"
                    data-testid="cancel-button"
                    onClick={() => {
                      this.setState({ modalOpen: true });
                    }}
                  >
                    {messages.cancel.defaultMessage}
                  </button>
                  <button
                    className="primary-button univers-67-bold-cond ml-16"
                    data-testid="save-button"
                    onClick={this.handleOnSave}
                  >
                    {updateBidDetailsLoading ? (
                      <Spinner
                        className="spinner"
                        fill="#ffffff"
                        height="16px"
                        width="16px"
                      />
                    ) : (
                      messages.save.defaultMessage
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const {
    bidDetails: {
      detailsLoading,
      detailsError,
      details,
      validationData,
      unsavedChanges,
      updateBidDetailsLoading,
      updateBidDetailsFailure,
      bid
    },
    bidSummary: { summary },
    user: { user },
    featureFlags
  } = state;
  return {
    detailsLoading,
    detailsError,
    details,
    validationData,
    unsavedChanges,
    updateBidDetailsLoading,
    updateBidDetailsFailure,
    bid,
    summary,
    user,
    featureFlags
  };
};

export default connect(mapStateToProps)(Details);
